<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import ReplySuggestionAPI from 'dashboard/api/captain/replySuggestion';
import NextButton from 'next/button/Button.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  canReply: { type: Boolean, default: true },
});

const { t } = useI18n();
const suggestion = ref(null);
const isReviewing = ref(false);
let loadVersion = 0;
let reviewVersion = 0;

const sources = computed(() => suggestion.value?.knowledge_sources || []);
const agentMetadata = computed(() => suggestion.value?.agent_metadata || {});
const isCaptainAgentSuggestion = computed(
  () => agentMetadata.value.generation_type === 'captain_agent'
);
const scenarioTitles = computed(() => agentMetadata.value.scenario_titles || []);

const loadSuggestion = async () => {
  const version = ++loadVersion;
  const conversationId = props.conversationId;
  suggestion.value = null;
  try {
    const { data } = await ReplySuggestionAPI.get(conversationId);
    if (version !== loadVersion || conversationId !== props.conversationId) return;
    suggestion.value = data?.status === 'pending' ? data : null;
  } catch (error) {
    if (version === loadVersion && conversationId === props.conversationId) suggestion.value = null;
  }
};

const onSuggestionUpdated = payload => {
  if (payload.conversation_id !== props.conversationId) return;
  // Revalidate queued socket events against current server-side eligibility.
  loadSuggestion();
};

const review = async action => {
  if (!suggestion.value || isReviewing.value) return;
  const conversationId = props.conversationId;
  const suggestionId = suggestion.value.id;
  const version = ++reviewVersion;
  try {
    isReviewing.value = true;
    await ReplySuggestionAPI[action](conversationId, suggestionId);
    if (version !== reviewVersion || conversationId !== props.conversationId) return;
    if (suggestion.value?.id === suggestionId) suggestion.value = null;
    const messageKey = action === 'approve' ? 'APPROVED' : 'REJECTED';
    useAlert(t(`CONVERSATION.REPLY_SUGGESTION.${messageKey}`));
  } catch (error) {
    if (version !== reviewVersion || conversationId !== props.conversationId) return;
    loadSuggestion();
    useAlert(error?.response?.data?.error || t('CONVERSATION.REPLY_SUGGESTION.ERROR'));
  } finally {
    if (version === reviewVersion) isReviewing.value = false;
  }
};

watch(() => props.conversationId, () => {
  reviewVersion += 1;
  isReviewing.value = false;
  loadSuggestion();
});
onMounted(() => {
  loadSuggestion();
  emitter.on(BUS_EVENTS.CAPTAIN_REPLY_SUGGESTION_UPDATED, onSuggestionUpdated);
});
onUnmounted(() => {
  loadVersion += 1;
  reviewVersion += 1;
  emitter.off(BUS_EVENTS.CAPTAIN_REPLY_SUGGESTION_UPDATED, onSuggestionUpdated);
});
</script>

<template>
  <div
    v-if="suggestion"
    class="mb-2 rounded-xl border border-n-brand/30 bg-n-brand/5 p-4"
  >
    <div class="mb-2 flex items-center justify-between gap-3">
      <div class="flex items-center gap-2 font-medium text-n-slate-12">
        <span class="i-lucide-sparkles text-n-brand" />
        {{ t('CONVERSATION.REPLY_SUGGESTION.TITLE') }}
      </div>
      <span v-if="isCaptainAgentSuggestion" class="text-xs text-n-slate-10">
        {{ t('CONVERSATION.REPLY_SUGGESTION.CAPTAIN_AGENT') }}
      </span>
      <span v-else class="text-xs text-n-slate-10">
        {{ t('CONVERSATION.REPLY_SUGGESTION.CONFIDENCE', { value: Math.round((suggestion.confidence || 0) * 100) }) }}
      </span>
    </div>
    <div
      v-if="scenarioTitles.length"
      class="mb-2 flex items-center gap-1.5 text-xs text-n-slate-10"
    >
      <span class="i-lucide-route" />
      {{ t('CONVERSATION.REPLY_SUGGESTION.SCENARIOS', { names: scenarioTitles.join(', ') }) }}
    </div>
    <p class="whitespace-pre-wrap text-sm text-n-slate-12">{{ suggestion.content }}</p>
    <div
      v-if="suggestion.translated_content"
      class="mt-3 border-t border-n-weak pt-3"
    >
      <div class="mb-1 flex items-center gap-1.5 text-xs text-n-slate-10">
        <span class="i-lucide-languages" />
        {{ t('CONVERSATION.REPLY_SUGGESTION.CHINESE_TRANSLATION') }}
      </div>
      <p class="whitespace-pre-wrap text-sm text-n-slate-11">
        {{ suggestion.translated_content }}
      </p>
    </div>
    <details v-if="sources.length" class="mt-3 text-xs text-n-slate-10">
      <summary class="cursor-pointer">
        {{ t('CONVERSATION.REPLY_SUGGESTION.SOURCES', { count: sources.length }) }}
      </summary>
      <ul class="mt-2 grid gap-2">
        <li v-for="source in sources" :key="source.id" class="rounded-lg bg-n-alpha-2 p-2">
          <strong>{{ source.question }}</strong>
          <p class="mt-1">{{ source.answer }}</p>
        </li>
      </ul>
    </details>
    <div class="mt-3 flex gap-2">
      <NextButton
        blue
        :disabled="!canReply || isReviewing"
        :is-loading="isReviewing"
        :label="t('CONVERSATION.REPLY_SUGGESTION.APPROVE')"
        @click="review('approve')"
      />
      <NextButton
        faded
        :disabled="isReviewing"
        :label="t('CONVERSATION.REPLY_SUGGESTION.REJECT')"
        @click="review('reject')"
      />
    </div>
  </div>
</template>
